<template>
    <div v-if="smartTerminalLogin" class="flex-center vh-100 pb-64">
        <SmartTerminalLoginRequired/>
    </div>
    <div class="quick-order-view">
        <template v-if="!isSearch">
            <StoreTopBar id="sticky-top-bar" :isPortalAllowed="goPortalEnabled"/>
            <StoreHeader id="store-header" v-resize="updateSize"/>
            <StoreNavBar
                v-if="productStore.categoryList.length > 0"
                :categories="productStore.categoryList"
                :activeCategory="activeCategory"
                @select-category="scrollToCategory"
                @toggle-search="enableSearch"/>
        </template>
        <div v-show="isSearch" style="position: sticky; top: 0; background: white; z-index: 9">
            <StoreSearch ref="searchResults" @toggle-search="disableSearch"/>
        </div>
        <div v-if="productStore.productList.length > 0" class="quick-order-view__products">
            <div id="full-product-list" class="px-16">
                <template v-if="visualSettings.gridLayout">
                    <VirtualScroller
                        v-if="!isSearch"
                        ref="scroller"
                        :items="productStore.scrollerGrid(gridColumns)"
                        :defaultItemSize="gridHeights"
                        :pageMode="true"
                        :skipHover="true"
                        :buffer="bannerOffset"
                        keyField="id">
                        <template #default="{ item }">
                            <ProductGridContainer
                                :items="item.items"
                                :columns="gridColumns"
                                @clickProduct="handleProductView"/>
                        </template>
                    </VirtualScroller>
                    <VirtualScroller
                        v-else-if="searchResults.searchResults.length > 0 && !searchResults.isSearchEmpty"
                        ref="scroller"
                        :key="searchResults.searchResults.length"
                        class="px-16"
                        :items="searchResults.searchResults"
                        :defaultItemSize="183"
                        :pageMode="true"
                        :skipHover="true"
                        :buffer="bannerOffset"
                        keyField="id">
                        <template #default="{ item }">
                            <ProductItem
                                :product="item"
                                :isStepperExpanded="false"
                                @clickProduct="handleProductView(item)"/>
                        </template>
                    </VirtualScroller>
                </template>
                <template v-else>
                    <VirtualScroller
                        v-if="!isSearch"
                        ref="scroller"
                        :items="productStore.scrollerList"
                        :defaultItemSize="183"
                        :pageMode="true"
                        :skipHover="true"
                        :buffer="bannerOffset"
                        keyField="id">
                        <template #default="{ item }">
                            <ProductItem
                                :product="item"
                                :isStepperExpanded="false"
                                @clickProduct="handleProductView(item)"/>
                        </template>
                    </VirtualScroller>
                    <VirtualScroller
                        v-else-if="searchResults.searchResults.length > 0 && !searchResults.isSearchEmpty"
                        ref="scroller"
                        :key="searchResults.searchResults.length"
                        :items="searchResults.searchResults"
                        :defaultItemSize="183"
                        :pageMode="true"
                        :skipHover="true"
                        :buffer="bannerOffset"
                        keyField="id">
                        <template #default="{ item }">
                            <ProductItem
                                :product="item"
                                :isStepperExpanded="false"
                                @clickProduct="handleProductView(item)"/>
                        </template>
                    </VirtualScroller>
                </template>
            </div>
            <ProductModal
                v-show="selectedProduct"
                class="quick-order-view__product-view"
                :product="selectedProduct"
                @close="removeProductQuery"/>
        </div>
        <OrderButton v-if="!isSearch" title="Review Order" :isBottomBar="true"/>
        <SideMenuAccount v-if="goPortalEnabled"/>
        <ThankYouModal
            v-if="computedQuery.orderNumber"
            v-once
            v-order-complete
            :orderNumber="computedQuery.orderNumber"
            :pickupTime="computedQuery.pickupTime"
            :paymentMethod="computedQuery.paymentMethod"
            :errorMessage="computedQuery.errors"
            :isPickupEnabled="goSettings.pickupEnabled"/>
    </div>
</template>
<script setup lang="ts">
import ProductItem from '../components/quick-order/ProductItem.vue';
import {useSettings} from '../compositions';
import {computed, Directive, nextTick, onBeforeMount, onMounted, ref} from 'vue';
import {toggleModalAnimation} from '../models/utilities/ToggleModalAnimation';
import {useRoute, useRouter} from 'vue-router';
import SmartTerminalLoginRequired from '../components/general/SmartTerminalLoginRequired.vue';
import SideMenuAccount from '../components/navigation/SideMenuAccount.vue';
import ThankYouModal from '../components/checkout/ThankYouModal.vue';
import Product from '../store/classes/Product';
import OrderButton from '../components/general/OrderButton.vue';
import VirtualScroller from '../components/VirtualScroller.vue';
import StoreSearch from '../components/quick-order/StoreSearch.vue';
import {gridHeight, useProductStore} from '../store/products';
import ProductModal from '../components/quick-order/ProductModal.vue';
import ProductGridContainer from '../components/quick-order/ProductGridContainer.vue';
import StoreHeader from '../components/quick-order/StoreHeader.vue';
import StoreTopBar from '../components/quick-order/StoreTopBar.vue';
import StoreNavBar from '../components/quick-order/StoreNavBar.vue';
import {LINKS} from '../scripts/constants';
import {useEventListener} from '../compositions/utilities/useEventListener';
import {useNotificationToast} from '../compositions/utilities/useNotificationToast';

const scroller = ref<InstanceType<typeof VirtualScroller>>(null);
const searchResults = ref<InstanceType<typeof StoreSearch>>(null);

const containerWidth = ref(getContainerWidth());
const stickyOffset = ref(0);
const bannerOffset = ref(0);

const gridColumns = ref(getGridColumns());

const gridHeights = computed(() => {
    return gridHeight(containerWidth.value, 16, gridColumns.value, 104);
});

const route = useRoute();
const router = useRouter();
const productStore = useProductStore();

const helcimStoreSubdomains = ['helcim-ca-hardware.helcim.app', 'helcim-us-hardware.helcim.app', 'tastyfood.helcim.app'];
const smartTerminalLogin = helcimStoreSubdomains.includes(window.location.hostname) && !route.query.businessClientId;

const { goSettings, visualSettings, portalSettings } = useSettings();
const { registerPassiveScroll, registerEvent } = useEventListener();
const { emitNotice } = useNotificationToast();

const selectedProduct = ref<Product>(Array.from(productStore.products.values())[0]);

//TODO: to remove the check when portal migration is finished
const goPortalEnabled = computed(() => {
    return typeof portalSettings.value !== 'undefined' ? portalSettings.value.enabled : goSettings.value.portalActive;
});

const computedQuery = computed(() => {
    if(!route.query) {
        return null;
    }
    return {
        orderNumber: typeof route.query.orderNumber === 'string' ? route.query.orderNumber : '',
        pickupTime: typeof route.query.pickupTime === 'string' ? route.query.pickupTime : '',
        errors: typeof route.query.errors === 'string' ? route.query.errors : '',
        paymentMethod: typeof route.query.paymentMethod === 'string' ? route.query.paymentMethod : '',
        search: typeof route.query.search === 'string' ? route.query.search : '',
        product: typeof route.query.product === 'string' ? route.query.product : '',
    };
});

const removeProductQuery = () => {
    const query = Object.assign({}, route.query);

    delete query.product;

    router.push({ path: route.path, query: query });
};

const isSearch = ref(false);

const disableSearch = () => {
    document.body.style.position = '';
    isSearch.value = false;
};

const enableSearch = () => {
    isSearch.value = true;
    nextTick(searchResults.value.focusSearch);
};

const virtualHeight = computed(() => {
    const map = new Map;
    let offset = 0;
    if(visualSettings.value.gridLayout) {
        productStore.scrollerGrid(gridColumns.value).forEach(section => {
            if(section.items[0]?.typez === 'cat') {
                map.set(section?.id, offset);
                offset += 56;
            } else {
                offset += gridHeights.value;
            }
        });
        return map;
    }
    productStore.categoryList.forEach(cat => {
        map.set(cat?.id, offset);
        offset += 56 + cat.products.length * 183;
    });
    return map;
});

const activeCategory = ref<number>(productStore.categoryList?.length > 0 ? productStore.categoryList[0]?.id : 0);

const highlight = () => {
    // TODO: Handle last categories based on remaining space

    // Handle highlight from top
    let select = virtualHeight.value.keys().next().value;
    for(const [k, v] of virtualHeight.value.entries()) {
        if(Math.floor(v) + bannerOffset.value >= Math.ceil(window.scrollY) + 1) {
            break;
        }
        select = k;
    }
    if(activeCategory.value !== select) {
        activeCategory.value = select;
    }
};

// TODO: Replace with scrollToItem template ref
const scrollToCategory = (id: number) => {
    window.scrollTo({
        top: bannerOffset.value + virtualHeight.value.get(id),
        behavior: 'smooth',
    });
};

const handleProductView = (product: Product, addParam = true) => {
    if(addParam) {
        router.push({ path: route.path, query: { ...route.query, product: product?.id } });
    }
    selectedProduct.value = product;
    toggleModalAnimation('product-view');
};

const vOrderComplete: Directive = {
    mounted() {
        if(computedQuery.value.orderNumber) {
            toggleModalAnimation('thank-you');
            if(computedQuery.value.errors) {
                nextTick(() => emitNotice(0, computedQuery.value.errors, [], 16000));
            }
            router.replace({ query: {} });
        }
    },
};

const storeActiveMiddleware = () => {
    if(!goSettings.value.onlineStoreActive) {
        if(goPortalEnabled.value) {
            router.push(LINKS.LOGIN);
            return;
        }
        window.location.href = 'https://helcim.com';
    }
};

const initializeOffsets = () => {
    const stickyTopBarElement = document.getElementById('sticky-top-bar') ?? null;
    const stickyHeaderElement = document.getElementById('sticky-header') ?? null;
    const storeHeaderElement = document.getElementById('store-header') ?? null;

    if (!stickyTopBarElement || !stickyHeaderElement || !storeHeaderElement) {
        return;
    }
    stickyOffset.value = stickyTopBarElement.offsetHeight + stickyHeaderElement.offsetHeight;
    bannerOffset.value = storeHeaderElement.offsetHeight;
};

const showProductByQuery = () => {
    if(computedQuery.value.product) {
        const product = productStore.products.get(+computedQuery.value.product);
        handleProductView(product, false);
    }
};

const updateSize = () => {
    bannerOffset.value = document.getElementById('store-header').offsetHeight;
};

function getContainerWidth() {
    const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    return width > 768 ? 768 : width;
}

function getGridColumns() {
    if (containerWidth.value < 414) {
        return 1;
    }
    if (containerWidth.value < 768) {
        return 2;
    }
    return 3;
}

const handleResize = () => {
    const width = getContainerWidth();
    if (width === containerWidth.value) {
        return;
    }
    containerWidth.value = width;
    gridColumns.value = getGridColumns();
};

onBeforeMount(storeActiveMiddleware);
onMounted(initializeOffsets);
onMounted(showProductByQuery);
registerPassiveScroll(highlight);
registerEvent('resize', handleResize);
</script>
<style lang="scss">
.quick-order-view {
    flex: auto 0 0;
}

.quick-order-view__header {
    flex: auto 0 0;
    position: sticky;
    top: 56px;
    z-index: 8;
    background-color: white;
    &__category-bar {
        background: var(--primary-100);
    }
    &__banner-message {
        display: flex;
        align-items: center;

        &__btn {
            cursor: pointer;
            color: white;
            text-decoration: underline;
        }
    }
}

.quick-order-view__products {
    flex: 100% 1 1;
    position: relative;
    padding-bottom: 24px;
}

.quick-order-view {
    .hds-o-drawer__menu {
        overflow-y: scroll;
    }
}
</style>
